import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import _ from "lodash";
import { BsModalService } from "ngx-bootstrap/modal";
import { firstValueFrom } from "rxjs";
import { AppState } from "../../../core/app-state";
import { RoleMgr } from "../../../core/role-mgr";
import { ConfirmModalComponent } from "../../../shared/components/confirm-modal/confirm-modal.component";
import { FesRutTestPointConfig, FesRutTestPointTestParams } from "../../models";
import { VolunteerTaskPointViewRecord } from "../../models/volunteer-task-point-record";
import { VolunteerTaskPointViewService } from "../../services/volunteer-task-point-view.service";
import { VolunteerTaskPointService } from "../../services/volunteer-task-point.service";
import { NgForm } from "@angular/forms";


export class InlineEdit<T> {
  bEditing: boolean;
  value: T;
  errorMsg?: string;

  constructor(
    public src: { 
      volunteerTaskPoint: VolunteerTaskPointViewRecord, 
      volunteerTaskPointViewService: VolunteerTaskPointViewService
    },
    public name: keyof VolunteerTaskPointViewRecord,
    public title: string,
    public defaultValue: T,
    public formattedValue: () => string = () => `${this.value}`
  ) { 
    this.value = this.getValueOrDefault();
  }

  beginEdit() {
    this.bEditing = true;
    this.errorMsg = undefined;
    this.value = this.getValueOrDefault();
  }

  hasCustomValue() {
    return this.getSrcValue() != null;
  }

  getSrcValue() {
    const srcVal = (this.src.volunteerTaskPoint as any)?.[this.name] as T;
    return srcVal;
  }

  getValueOrDefault() {
    return this.getSrcValue() ?? this.defaultValue;
  }

  save = async () => {
    this.errorMsg = undefined;
    const tpInput = {
      id: this.src.volunteerTaskPoint?.id,
      //revisionId: this.src.volunteerTaskPoint?.revisionId,
    };
    (tpInput as any)[this.name] = this.value;
    const patchedVal = await this.src.volunteerTaskPointViewService.patchTaskPoint(tpInput);
    (this.src.volunteerTaskPoint as any)[this.name] = patchedVal[this.name];
    return patchedVal;
  }

  updateValue(v: T|undefined) {
    this.errorMsg = undefined;
    //TBD - when to override this.src.volunteerTaskPoint[this.name]?
    this.value = v;
    return this.value;
  }

  reset() {
    const rtnVal = this.value;
    this.bEditing = false;
    this.errorMsg = undefined;
    this.value = this.getValueOrDefault();
    return rtnVal;
  }
};

export const extractTestCallParams = ( vtp: VolunteerTaskPointViewRecord ) => ({
  validation: {
    qr: vtp.qrValidationRequired,
    photo: vtp.photoValidationRequired,
    computeFix: vtp.computeFixValidationRequired,
  },
  call: {
    durationSecs: vtp.targetCallDurationSecs,
    dialNumber: vtp.dialNumber,
    count: vtp.targetCallCnt,
    intervalSecs: vtp.callIntervalSecs
  }
});

@Component({
  selector: "app-volunteer-task-point-test-params",
  templateUrl: "volunteer-task-point-test-params.component.html",
})
export class VolunteerTaskPointTestParamsComponent implements OnInit {
  
  @Input() bFormMode = false; // not inline edits so show a save and cancel button
  @Input() bShowFormButtons = false;

  @Input('taskPoint') set taskPointSetter(vtp: VolunteerTaskPointViewRecord) {
    this.resetInlineEdits();
    this.volunteerTaskPoint = vtp;
  }

  volunteerTaskPoint = new VolunteerTaskPointViewRecord();

  @Input() config: FesRutTestPointConfig;

  @Output() taskPointModified = new EventEmitter<VolunteerTaskPointViewRecord>();
  @Output() formSubmitEmitter = new EventEmitter<FesRutTestPointTestParams>();

  @ViewChild('paramsForm') paramsForm: NgForm;
  
  targetCallCnt: InlineEdit<number>;
  targetCallDurationSecs: InlineEdit<number>;
  callIntervalSecs: InlineEdit<number>;
  dialNumber: InlineEdit<string>;
  qrValidationRequired: InlineEdit<boolean>;
  photoValidationRequired: InlineEdit<boolean>;
  computeFixValidationRequired: InlineEdit<boolean>;


  saveTaskPointErrorMsg = '';
  loading = false;
  editComponents: InlineEdit<any>[] = [];


  constructor(
    protected appState: AppState,
    public roleMgr: RoleMgr,
    public volunteerTaskPointService: VolunteerTaskPointService,
    public volunteerTaskPointViewService: VolunteerTaskPointViewService,
    public bsModalService: BsModalService,
  ) {
  }

  ngOnInit(): void {
    this.targetCallCnt = new InlineEdit<number>(this, 'targetCallCnt', 'call count', 
      this.volunteerTaskPoint?.targetCallCnt ?? this.config.defaults.call.count);
    this.targetCallDurationSecs = new InlineEdit<number>(this, 'targetCallDurationSecs', 'call duration', 
      this.volunteerTaskPoint?.targetCallDurationSecs ?? this.config.defaults.call.durationSecs);
    this.callIntervalSecs = new InlineEdit<number>(this, 'callIntervalSecs', 'call interval', 
      this.volunteerTaskPoint?.callIntervalSecs ?? this.config.defaults.call.intervalSecs);
    this.dialNumber = new InlineEdit<string>(this, 'dialNumber', 'destination phone number', 
      this.volunteerTaskPoint?.dialNumber ?? this.config.defaults.call.dialNumber);
    this.qrValidationRequired = new InlineEdit<boolean>(this, 'qrValidationRequired', 'QR scan required', 
      this.volunteerTaskPoint?.qrValidationRequired ?? this.config.defaults.validation.qr);
    this.photoValidationRequired = new InlineEdit<boolean>(this, 'photoValidationRequired', 'Photo at test point required', 
      this.volunteerTaskPoint?.photoValidationRequired ?? this.config.defaults.validation.photo);
    this.computeFixValidationRequired = new InlineEdit<boolean>(this, 'computeFixValidationRequired', 'Phone should compute location after call', 
      this.volunteerTaskPoint?.computeFixValidationRequired ?? this.config.defaults.validation.computeFix);    
    this.editComponents.push(
      this.targetCallCnt,
      this.targetCallDurationSecs,
      this.callIntervalSecs,
      this.dialNumber,
      this.qrValidationRequired,
      this.photoValidationRequired,
      this.computeFixValidationRequired,
    );
    if ( this.bFormMode ) {
      this.editComponents.forEach(c=>c.beginEdit());
    }

  }

  async onSaveInlineEdit<T>(inlineEdit: InlineEdit<T>, eventTarget?: EventTarget) {
    if ( this.bFormMode ) {
      // Changes will be saved in bulk like a form with a submit    
      return;
    }
    if ( eventTarget ) {
      // hack to handle checkboxes (grab latest value directly from html element)
      const { checked } = eventTarget as HTMLInputElement;
      inlineEdit.value = checked as unknown as T;
      if ( !inlineEdit.src.volunteerTaskPoint?.id ) {
        // allow checkbox updates that will be saved later (after task point is assigned)
        return
      }
    }
    try {
      this.loading = true;
      const taskPoint = await inlineEdit.save();
      console.log('>>>', taskPoint, inlineEdit.name, inlineEdit.value);
      this.appState.notifySuccess(`${inlineEdit.title}: ${inlineEdit.formattedValue()}`, this, 2);
      inlineEdit.reset();
      this.taskPointModified.next(taskPoint);
      //await this.onRefresh();
    } catch (e: any) {
      console.error(e);
      const msg = this.volunteerTaskPointService.extractErrorMsg(e);
      this.appState.notifyError(msg, this, 2);
      inlineEdit.errorMsg = `Error saving ${inlineEdit.name} for point. ${msg}`;
    } finally {
      this.loading = false;
    }
  }

  async useDefaultValue<T>(inlineEdit: InlineEdit<T>) {
    if ( !inlineEdit.hasCustomValue() ) {
      return;
    }
    const modalRef = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      id: 202308014,
      initialState: {
        onYes: async () => {
          inlineEdit.value = null;
          await this.onSaveInlineEdit(inlineEdit);
        },
        question: `Clear custom value and use global default '${inlineEdit.defaultValue}'?`,
        title: `Use default for ${inlineEdit.title}?`,
      },
    });
    await firstValueFrom(modalRef.onHidden)

  }

  getChanges = () => {
    return this.editComponents.filter(editComp=>editComp.value!=editComp.defaultValue);
  }

  getFormData = (): FesRutTestPointTestParams => {
    return {
      call: {
        durationSecs: this.targetCallDurationSecs.value,
        dialNumber: this.dialNumber.value,
        count: this.targetCallCnt.value,
        intervalSecs: this.callIntervalSecs.value,
      },
      validation: {
        qr: this.qrValidationRequired.value,
        photo: this.photoValidationRequired.value,
        computeFix: this.computeFixValidationRequired.value,
      }

    }
  }
  
  resetInlineEdits() {
    this.editComponents.forEach(c=>c?.reset());
  }

}
